import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { surface, tone } from '../timeclock/tcFormat';
import { addDays, locale, mondayOf, parseDate, teamError, teamPost } from './teamFormat';

/**
 * The existing weekly schedule (VIP of the day, breaks, cleaning, home
 * working; edited under "user-info") next to the planner: the VIP of the
 * duty week, home working by the rules, and who is away but named in the
 * schedule. Management can take the planner's VIP and home days over.
 */
const TeamWeekTab = ({ t, lang, theme, token, me, reloadKey, notify, failed }) => {
    const [from, setFrom] = useState(mondayOf(me.today));
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [fillNotes, setFillNotes] = useState([]);

    useEffect(() => {
        let alive = true;
        setData(null);
        setFillNotes([]);
        teamPost('week', token, { from }).then((response) => {
            if (!alive) return;
            if (response?.success) {
                setData(response.data);
                setError('');
            } else {
                setError(failed(response));
            }
        });
        return () => { alive = false; };
    }, [token, from, reloadKey, failed]);

    const apply = () => {
        setBusy(true);
        teamPost('applyWeek', token, { from }).then((response) => {
            setBusy(false);
            if (response?.success) {
                setData(response.data);
                notify(t('team_week_applied', 'Weekly schedule updated.'), true);
            } else {
                notify(teamError(t, response), false);
            }
        });
    };

    // Management: fill in this week's schedule from the planner (only the first week without one).
    const fill = () => {
        if (!window.confirm(t('team_week_fill_confirm', 'Fill in this week\'s schedule automatically? You can still change it under user-info afterwards.'))) return;
        setBusy(true);
        teamPost('fillWeek', token, { from }).then((response) => {
            setBusy(false);
            if (response?.success) {
                setData(response.data);
                setFillNotes(response.data.fill_notes || []);
                notify(t('team_week_filled', 'Weekly schedule filled in.'), true);
            } else {
                notify(teamError(t, response), false);
            }
        });
    };

    const s = surface(theme);
    const loc = locale(lang);
    const role = (r) => ({
        vip: t('team_role_vip', 'VIP of the day'),
        first_break: t('team_role_first_break', 'in the first break'),
        last_break: t('team_role_last_break', 'in the last break'),
        cleaning: t('team_role_cleaning', 'on cleaning duty'),
    }[r] || r);
    const warning = (w) => {
        const p = w.params || {};
        switch (w.key) {
            case 'vip_differs': return t('team_w_vip', { defaultValue: 'The schedule has {{schedule}} as VIP; the duty week says {{duty}}.', schedule: p.schedule || '—', duty: p.duty });
            case 'away_in_role': return t('team_w_away', { defaultValue: '{{name}} is away but is {{role}}.', name: p.name, role: role(p.role) });
            case 'home_dev_differs': return p.planner ? t('team_w_home_dev_on', 'By the rule development works from home; the schedule does not say so.') : t('team_w_home_dev_off', 'The schedule says development works from home; by the rule not today.');
            case 'home_support_differs': return p.planner ? t('team_w_home_sup_on', 'By the rule support works from home; the schedule does not say so.') : t('team_w_home_sup_off', 'The schedule says support works from home; by the rule not today.');
            default: return w.key;
        }
    };
    const list = (v) => (Array.isArray(v) ? v.filter(Boolean).join(', ') : v) || '—';

    return (
        <Box sx={{ maxWidth: 1000 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <IconButton size="small" onClick={() => setFrom(addDays(from, -7))}><ChevronLeft /></IconButton>
                <Typography sx={{ fontSize: 16, fontWeight: 700, minWidth: 220, textAlign: 'center' }}>
                    {data ? t('team_week_short', 'wk') + ' ' + data.iso_week + ' · ' : ''}
                    {parseDate(from).toLocaleDateString(loc, { day: 'numeric', month: 'short' })} – {parseDate(addDays(from, 4)).toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' })}
                </Typography>
                <IconButton size="small" onClick={() => setFrom(addDays(from, 7))}><ChevronRight /></IconButton>
                {from !== mondayOf(me.today) && <Button size="small" onClick={() => setFrom(mondayOf(me.today))} sx={{ textTransform: 'none' }}>{t('team_today', 'Today')}</Button>}
                <Box sx={{ flex: 1 }} />
                {data?.can_fill && (
                    <Button variant="contained" disableElevation disabled={busy} onClick={fill} sx={{ textTransform: 'none' }}>
                        {t('team_week_fill', 'Fill in this week automatically')}
                    </Button>
                )}
                {data?.can_apply && (
                    <Button variant="contained" disableElevation disabled={busy} onClick={apply} sx={{ textTransform: 'none' }}>
                        {t('team_week_apply', 'Take VIP and home working over into the schedule')}
                    </Button>
                )}
            </Box>
            {data?.weekend && (
                <Typography sx={{ fontSize: 13.5, mb: 1 }}>
                    <b>{t('team_weekend_duty', 'Weekend duty')}:</b> {data.weekend}
                    {data.weekend_swapped && <Box component="span" sx={{ color: tone.info }}> · {t('team_weekend_other', 'other than the VIP')}</Box>}
                </Typography>
            )}
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
                {t('team_week_note', 'The weekly schedule itself is still edited under "user-info". The planner fills in the VIP from the duty week and home working from the rules, and warns when someone who is away is named.')}
            </Typography>
            {error && <Typography sx={{ color: tone.bad, fontSize: 13.5, mb: 1 }}>{error}</Typography>}
            {!data && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
            {data && !data.has_schedule && (
                <Box sx={{ ...s.card, p: 2, mb: 2 }}>
                    <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>{t('team_week_none', 'There is no weekly schedule for this week yet; below is what the planner knows.')}</Typography>
                    {data.can_fill && (
                        <Typography sx={{ fontSize: 13.5, mt: 0.75 }}>
                            {t('team_week_fill_hint', 'Use "Fill in this week automatically": VIP of the duty week, breaks shifted on from last week, the cleaning crew of the month and home working, taking leave and rosters into account.')}
                        </Typography>
                    )}
                    {!data.can_fill && data.next_fill_monday && data.next_fill_monday !== from && (
                        <Button size="small" onClick={() => setFrom(data.next_fill_monday)} sx={{ textTransform: 'none', mt: 0.75, px: 0 }}>
                            {t('team_week_fill_goto', 'The first week to fill in automatically is the week of')} {parseDate(data.next_fill_monday).toLocaleDateString(loc, { day: 'numeric', month: 'short' })} →
                        </Button>
                    )}
                </Box>
            )}
            {fillNotes.length > 0 && (
                <Box sx={{ ...s.card, p: 2, mb: 2, borderLeft: '4px solid ' + tone.info }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>{t('team_week_fill_notes', 'Filled in; worth checking:')}</Typography>
                    {fillNotes.map((n, i) => <Typography key={i} sx={{ fontSize: 13.5 }}>• {n}</Typography>)}
                </Box>
            )}
            {data && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' }, gap: 1.5 }}>
                    {data.days.map((d) => {
                        const sc = d.schedule;
                        const p = d.planner;
                        return (
                            <Box key={d.day} sx={{ ...s.card, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1,
                                borderTop: '3px solid ' + (d.warnings.length ? tone.warn : d.holiday ? s.ghost : tone.ok) }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, textTransform: 'capitalize' }}>
                                    {parseDate(d.day).toLocaleDateString(loc, { weekday: 'long', day: 'numeric', month: 'short' })}
                                </Typography>
                                {d.holiday && <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{d.holiday}</Typography>}
                                <Box sx={{ fontSize: 12.5, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.25 }}>
                                    <Box sx={{ color: s.faint }}>{t('team_vip_short', 'VIP')}</Box>
                                    <Box>{sc ? sc.responsible || '—' : '—'}{p.vip && (!sc || sc.responsible !== p.vip) ? <Box component="span" sx={{ color: tone.info }}> → {p.vip}</Box> : null}</Box>
                                    <Box sx={{ color: s.faint }}>{t('team_break1', 'Break 1')}</Box><Box>{sc ? list(sc.first_break) : '—'}</Box>
                                    <Box sx={{ color: s.faint }}>{t('team_break2', 'Break 2')}</Box><Box>{sc ? list(sc.last_break) : '—'}</Box>
                                    <Box sx={{ color: s.faint }}>{t('team_cleaning', 'Cleaning')}</Box><Box>{sc ? list(sc.cleaning) : '—'}</Box>
                                    <Box sx={{ color: s.faint }}>⌂</Box>
                                    <Box>
                                        {[p.home_dev ? t('team_dept_dev', 'Development') : null, p.home_support ? t('team_dept_support', 'Support') : null].filter(Boolean).join(', ') || '—'}
                                    </Box>
                                    <Box sx={{ color: s.faint }}>{t('team_away_short', 'Away')}</Box><Box>{p.away.length ? p.away.join(', ') : '—'}</Box>
                                </Box>
                                {d.warnings.map((w, i) => (
                                    <Typography key={i} sx={{ fontSize: 12.5, color: tone.warn, lineHeight: 1.4 }}>! {warning(w)}</Typography>
                                ))}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default TeamWeekTab;
